import { makeStyles } from 'tss-react/mui';

export const SEARCH_WIDTH = 680;
export const DROPDOWN_WIDTH = SEARCH_WIDTH;

export const useStyles = makeStyles()((theme) => ({
    root: {
        position: 'relative',
        width: SEARCH_WIDTH,
        maxWidth: '100%',
    },
    input: {
        width: '100%',
        backgroundColor: theme.palette.background.paper,
    },
    dropdown: {
        zIndex: theme.zIndex.modal,
        width: DROPDOWN_WIDTH,
        maxWidth: '100vw',
    },
    paper: {
        marginTop: theme.spacing(0.5),
        maxHeight: 'min(560px, 70vh)',
        overflowY: 'auto',
        borderRadius: theme.spacing(1),
        boxShadow: theme.shadows[8],
        border: `1px solid ${theme.palette.divider}`,
    },
    refetchBar: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    // Group headers
    groupHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2, 1.25),
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.06em',
        fontWeight: 600,
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    groupIcon: {
        fontSize: '1rem',
        color: theme.palette.text.secondary,
        flexShrink: 0,
    },
    groupCount: {
        fontSize: '0.65rem',
        fontWeight: 500,
        lineHeight: 1,
        padding: theme.spacing(0.25, 0.75),
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.secondary,
        letterSpacing: 0,
        textTransform: 'none' as const,
    },
    groupDivider: {
        margin: 0,
    },
    // Result rows
    resultRow: {
        alignItems: 'flex-start',
        minHeight: 56,
        padding: theme.spacing(1, 2),
        gap: theme.spacing(1.5),
        overflow: 'hidden',
        '&.Mui-selected': {
            backgroundColor: theme.palette.action.selected,
            boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
        },
        '&.Mui-selected:hover': {
            backgroundColor: theme.palette.action.selected,
        },
    },
    resultAvatar: {
        width: 32,
        height: 32,
        marginTop: theme.spacing(0.25),
        fontSize: '0.75rem',
        flexShrink: 0,
    },
    resultAvatarInstance: {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
    },
    resultAvatarAddon: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
    },
    resultAvatarUser: {
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.secondary.contrastText,
    },
    resultAvatarSupportPackage: {
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.contrastText,
    },
    resultAvatarWorkspace: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.contrastText,
    },
    resultAvatarDataset: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
    },
    resultAvatarSelected: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    resultText: {
        minWidth: 0,
        flex: 1,
        overflow: 'hidden',
    },
    resultSecondaryContainer: {
        minWidth: 0,
        overflow: 'hidden',
    },
    resultPrimary: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    resultSecondary: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    resultTertiary: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '0.7rem',
        color: theme.palette.text.disabled,
        marginTop: theme.spacing(0.25),
        fontFamily: 'monospace',
    },
    resultChipsRow: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: theme.spacing(0.5),
        alignItems: 'center',
        marginTop: theme.spacing(0.5),
        maxWidth: '100%',
        overflow: 'hidden',
    },
    resultMeta: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
        flexShrink: 0,
        paddingTop: theme.spacing(0.25),
    },
    noResultsInSection: {
        padding: theme.spacing(1, 2),
        color: theme.palette.text.disabled,
        fontSize: '0.8rem',
        fontStyle: 'italic',
    },
    moreMatchesRow: {
        display: 'block',
        padding: theme.spacing(0.75, 2, 1),
        color: theme.palette.text.disabled,
        fontSize: '0.7rem',
        fontStyle: 'italic',
    },
    stateChip: {
        height: 18,
        fontSize: '0.65rem',
        '& .MuiChip-label': {
            padding: theme.spacing(0, 0.75),
        },
    },
    badgeChip: {
        height: 18,
        fontSize: '0.65rem',
        '& .MuiChip-label': {
            padding: theme.spacing(0, 0.75),
        },
    },
    visitIcon: {
        fontSize: '1rem',
        color: theme.palette.text.disabled,
        opacity: 0.4,
        transition: 'opacity 150ms, color 150ms',
        '.Mui-selected &': {
            opacity: 1,
            color: theme.palette.primary.main,
        },
    },
    // Loading skeletons
    skeletonRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 2),
        minHeight: 56,
    },
    skeletonText: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: theme.spacing(0.75),
        paddingTop: theme.spacing(0.5),
    },
    // Empty / prompt states
    emptyState: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4, 2),
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
        textAlign: 'center' as const,
    },
    emptyIcon: {
        fontSize: '2rem',
        color: theme.palette.text.disabled,
    },
    promptRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        color: theme.palette.text.disabled,
    },
    filterDialogPaper: {
        borderRadius: theme.spacing(1.5),
    },
    filterDialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    filterDialogTitleText: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: '1.125rem',
        fontWeight: 600,
    },
    filterDialogContent: {
        backgroundColor: theme.palette.background.default,
    },
    filterDialogActions: {
        padding: theme.spacing(1.5, 3),
    },
    filterGroupsList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: theme.spacing(2),
    },
    filterGroupRow: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5, 1.5),
    },
    filterFieldsRow: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: theme.spacing(2.5),
    },
    filterFieldColumn: {
        flex: '1 1 200px',
        minWidth: 200,
    },
    filterColumnHeader: {
        marginBottom: theme.spacing(0.5),
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.06em',
    },
    filterDialogTabs: {
        paddingLeft: theme.spacing(3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    filterEmptyState: {
        padding: theme.spacing(4, 2),
        textAlign: 'center' as const,
    },
    filterFieldHeader: {
        fontSize: '0.7rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        marginBottom: theme.spacing(0.25),
    },
}));
